import { IconEdit } from "@/assets/icons/geist/IconEdit";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { IconUserAdd } from "@/assets/icons/geist/IconUserAdd";
import { IconKey } from "@/assets/icons/geist/IconKey";
import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { cn } from "@/components/podkit/lib/cn";
import { Pill } from "@/components/podkit/status/Pill";
import { GroupAvatar } from "@/components/settings/organization/groups/GroupAvatar";
import { MemberAvatarStack } from "@/components/settings/organization/groups/MemberAvatarStack";
import { DeleteGroupDialog } from "@/components/settings/organization/groups/DeleteGroupDialog";
import { ManageOrgRolesDialog } from "@/components/settings/organization/groups/ManageOrgRolesDialog";
import { useListGroupMemberships, useListRoleAssignments } from "@/queries/group-queries";
import { useMembers, useOrganization } from "@/queries/organization-queries";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useFlagOrgRolesEnabled } from "@/hooks/use-flag";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Group, ResourceRole } from "gitpod-next-api/gitpod/v1/group_pb";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { MoreHorizontal } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";

type GroupCardProps = {
    group: PlainMessage<Group>;
    onAddMembers: () => void;
    onEdit: () => void;
};

// Map ResourceRole enum values to display labels
const ORG_ROLE_LABELS: Partial<Record<ResourceRole, string>> = {
    [50 as ResourceRole]: "Runners Admin", // ResourceRole.ORG_RUNNERS_ADMIN = 50
    [51 as ResourceRole]: "Projects Admin", // ResourceRole.ORG_PROJECTS_ADMIN = 51
};

export const GroupCard: FC<GroupCardProps> = ({ group, onAddMembers, onEdit }) => {
    // Fetch real membership data for the group
    const { data: membersData } = useMembers();
    const { data: organization } = useOrganization();
    const { data: groupMemberships = [] } = useListGroupMemberships(group.id);
    const { hasPermission } = useResourcePermissions();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showOrgRolesDialog, setShowOrgRolesDialog] = useState(false);
    const { value: orgRolesEnabled } = useFlagOrgRolesEnabled();

    // Fetch org-level role assignments for this group
    const { data: orgRoleAssignments } = useListRoleAssignments({
        resourceType: ResourceType.ORGANIZATION,
        resourceId: organization?.id ?? "",
        groupId: group.id,
        enabled: orgRolesEnabled && !!organization?.id,
    });

    const assignedOrgRoles = useMemo(() => {
        if (!orgRoleAssignments?.assignments) return [];
        return orgRoleAssignments.assignments
            .map((a) => ({
                role: a.resourceRole,
                label: ORG_ROLE_LABELS[a.resourceRole] ?? `Role ${a.resourceRole}`,
            }))
            .filter((r) => ORG_ROLE_LABELS[r.role]); // Only show roles we have labels for
    }, [orgRoleAssignments?.assignments]);

    const canAddMembers = hasPermission(ResourceType.GROUP, group.id, "groupmembership:create");
    const canEdit = hasPermission(ResourceType.GROUP, group.id, "group:update");
    const canDelete = hasPermission(ResourceType.GROUP, group.id, "group:delete");
    const canManageOrgRoles = hasPermission(ResourceType.ORGANIZATION, "", "org:update");

    const handleAddMembers = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onAddMembers();
        },
        [onAddMembers],
    );

    const handleEdit = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onEdit();
        },
        [onEdit],
    );

    const handleDeleteClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteDialog(true);
    }, []);

    const handleManageOrgRoles = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setShowOrgRolesDialog(true);
    }, []);

    return (
        <>
            <Link
                to={`/settings/members/group/${group.id}`}
                className={cn(
                    "flex w-full max-w-[400px] cursor-pointer flex-col gap-4 rounded-xl border-0.5 border-solid border-border-base bg-surface-glass px-5 py-5 text-left text-xs",
                    "hover:outline hover:outline-1 hover:outline-border-brand focus-visible:ring-1",
                )}
                aria-label="View Group Details"
                data-tracking-id="view-group-details-group-card"
            >
                {/* Header: Group avatar (left) ... Actions dropdown (right) */}
                <div className="flex flex-row">
                    <div className="grow">
                        <GroupAvatar groupName={group.name} size="sm" />
                    </div>
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        data-tracking-id-none
                    >
                        <DropdownActions
                            button={
                                <button
                                    className="flex size-8 items-center justify-center rounded-md hover:bg-surface-secondary"
                                    aria-label="Group actions"
                                    data-tracking-id="group-actions"
                                >
                                    <MoreHorizontal className="size-4 text-content-tertiary" />
                                </button>
                            }
                            align="end"
                        >
                            <DropdownMenuItem
                                key="add-members"
                                data-tracking-id="group-add-member"
                                disabled={group.systemManaged || !canAddMembers}
                                onClick={handleAddMembers}
                            >
                                <IconUserAdd size="sm" className="mr-2" />
                                Add Members
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                key="edit"
                                data-tracking-id="group-edit"
                                disabled={group.systemManaged || !canEdit}
                                onClick={handleEdit}
                            >
                                <IconEdit size="sm" className="mr-2" />
                                Edit
                            </DropdownMenuItem>
                            {orgRolesEnabled && (
                                <DropdownMenuItem
                                    key="manage-org-roles"
                                    data-tracking-id="group-manage-org-roles"
                                    disabled={group.systemManaged || !canManageOrgRoles}
                                    onClick={handleManageOrgRoles}
                                >
                                    <IconKey size="sm" className="mr-2" />
                                    Manage Org Roles
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                key="delete"
                                data-tracking-id="group-delete"
                                className="text-content-danger"
                                disabled={group.systemManaged || !canDelete}
                                onClick={handleDeleteClick}
                            >
                                <IconTrash size="sm" className="mr-2" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownActions>
                    </div>
                </div>

                {/* Body: Group name and description with 2px gap between them */}
                <div className="flex min-w-0 flex-col gap-0.5">
                    <h3 className="truncate text-md font-semibold text-content-primary">{group.name}</h3>
                    <p className="truncate text-base text-content-secondary">{group.description || "\u00A0"}</p>
                </div>

                {/* Member avatars section */}
                <div className="flex flex-row gap-2">
                    <MemberAvatarStack
                        orgMembers={membersData?.members}
                        memberships={groupMemberships}
                        maxVisible={3}
                        size="sm"
                    />
                </div>

                {/* Org roles badges */}
                {orgRolesEnabled && assignedOrgRoles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {assignedOrgRoles.map((r) => (
                            <Pill key={r.role} variant="info">
                                {r.label}
                            </Pill>
                        ))}
                    </div>
                )}
            </Link>

            {/* Delete Group Dialog - Rendered outside the Link */}
            <DeleteGroupDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog} group={group} />

            {/* Manage Org Roles Dialog */}
            {orgRolesEnabled && (
                <ManageOrgRolesDialog
                    group={group}
                    open={showOrgRolesDialog}
                    onClose={() => setShowOrgRolesDialog(false)}
                />
            )}
        </>
    );
};
